// Decathlon IAAF points conversion (118). Pure + unit-tested.
//
// Men's decathlon, 10 events. The World Athletics combined-events formula:
//   track (running): points = floor(A * (B - P)^C),  P = time in seconds
//   field (jump/throw): points = floor(A * (P - B)^C), P = performance
// Performance units are per-event "native" (the unit the B coefficient uses):
// runs in SECONDS, jumps (LJ/HJ/PV) in CENTIMETRES, throws (SP/discus/javelin)
// in METRES. A non-improving performance (base <= 0) scores 0.
//
// Heptathlon uses different (women's) coefficients and is deferred.

export type DecathlonEventKind = "track" | "field";

export interface DecathlonDiscipline {
  key: string;
  label: string;
  kind: DecathlonEventKind;
  unit: "seconds" | "cm" | "metres";
  A: number;
  B: number;
  C: number;
}

// Order = standard decathlon sequence (events 1-5 day one, 6-10 day two).
export const DECATHLON_DISCIPLINES: DecathlonDiscipline[] = [
  { key: "100m", label: "100m", kind: "track", unit: "seconds", A: 25.4347, B: 18, C: 1.81 },
  { key: "long_jump", label: "Long Jump", kind: "field", unit: "cm", A: 0.14354, B: 220, C: 1.4 },
  { key: "shot_put", label: "Shot Put", kind: "field", unit: "metres", A: 51.39, B: 1.5, C: 1.05 },
  { key: "high_jump", label: "High Jump", kind: "field", unit: "cm", A: 0.8465, B: 75, C: 1.42 },
  { key: "400m", label: "400m", kind: "track", unit: "seconds", A: 1.53775, B: 82, C: 1.81 },
  { key: "110m_hurdles", label: "110m Hurdles", kind: "track", unit: "seconds", A: 5.74352, B: 28.5, C: 1.92 },
  { key: "discus", label: "Discus", kind: "field", unit: "metres", A: 12.91, B: 4, C: 1.1 },
  { key: "pole_vault", label: "Pole Vault", kind: "field", unit: "cm", A: 0.2797, B: 100, C: 1.35 },
  { key: "javelin", label: "Javelin", kind: "field", unit: "metres", A: 10.14, B: 7, C: 1.08 },
  { key: "1500m", label: "1500m", kind: "track", unit: "seconds", A: 0.03768, B: 480, C: 1.85 },
];

const BY_KEY = new Map(DECATHLON_DISCIPLINES.map((d) => [d.key, d]));

export function decathlonDiscipline(key: string): DecathlonDiscipline | undefined {
  return BY_KEY.get(key);
}

/**
 * IAAF points for a single decathlon event. `performance` is in the discipline's
 * native unit (see DECATHLON_DISCIPLINES). Returns 0 for an unknown discipline or
 * a performance that does not clear the formula's threshold.
 */
export function decathlonPoints(disciplineKey: string, performance: number): number {
  const d = BY_KEY.get(disciplineKey);
  if (!d) return 0;
  const base = d.kind === "track" ? d.B - performance : performance - d.B;
  if (base <= 0) return 0;
  return Math.floor(d.A * Math.pow(base, d.C));
}
